import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import { lucideCheck, lucideMapPin } from '@ng-icons/lucide';
import { HlmInputDirective } from '@spartan-ng/helm/input';
import type { Address } from '@shared/models/address.model';

/**
 * Multi-select counterpart of the analysis dialog's address picker: the same
 * search field and result rows, but rows toggle instead of closing the panel,
 * and everything picked is listed underneath so a long selection stays reviewable
 * without reopening the dropdown.
 *
 * Presentational — the host owns loading, searching and the selection set.
 *
 * @example
 * <app-address-multi-select
 *   [addresses]="filtered()" [selectedIds]="selectedIds()" [totalCount]="total()"
 *   (searchChanged)="onSearch($event)" (toggled)="toggle($event)" />
 */
@Component({
  selector: 'app-address-multi-select',
  standalone: true,
  imports: [NgIconComponent, HlmInputDirective],
  providers: [provideIcons({ lucideCheck, lucideMapPin })],
  host: {
    '(document:click)': 'onDocumentClick($event)',
  },
  template: `
    <div class="flex flex-col gap-4">
      <!-- Search + results -->
      <div class="relative">
        <input
          hlmInput
          type="text"
          placeholder="Straße, PLZ oder Ort suchen"
          [value]="query()"
          (input)="onInput($event)"
          (focus)="open.set(true)"
        />

        @if (open()) {
          <div
            class="ks-menu-panel absolute left-0 right-0 top-[calc(100%+4px)] z-40 overflow-hidden p-0"
          >
            <div class="max-h-64 overflow-y-auto">
              @for (address of addresses(); track address.addressId) {
                <button
                  type="button"
                  class="flex w-full items-center gap-3 border-b border-border/60 px-3 py-2.5 text-left transition-colors last:border-b-0 hover:bg-muted"
                  (click)="toggled.emit(address)"
                >
                  <span class="min-w-0 flex-1">
                    <span class="block truncate text-sm font-normal text-foreground">
                      {{ address.street }} {{ address.houseNumber }}
                    </span>
                    <span class="mt-px block font-mono text-2xs text-muted-foreground">
                      {{ address.postalCode }} {{ address.city }}
                    </span>
                  </span>
                  @if (isSelected(address)) {
                    <ng-icon name="lucideCheck" class="h-4 w-4 shrink-0 text-primary" />
                  }
                </button>
              } @empty {
                <p class="px-3 py-3.5 text-xs text-muted-foreground">
                  {{ isLoading() ? 'Adressen werden geladen…' : 'Keine Adresse gefunden.' }}
                </p>
              }
            </div>

            <div class="flex items-center justify-between bg-muted px-3 py-2">
              <span class="font-mono text-2xs text-muted-foreground">{{ countLine() }}</span>
              <button
                type="button"
                class="text-2xs font-bold text-primary hover:text-primary/80"
                (click)="open.set(false)"
              >
                Schließen
              </button>
            </div>
          </div>
        }
      </div>

      @if (error()) {
        <p class="text-xs text-destructive">{{ error() }}</p>
      }

      <!-- Everything picked so far -->
      @if (selectedAddresses().length > 0) {
        <div class="flex flex-col gap-2">
          <div class="flex items-baseline justify-between">
            <span class="text-xs font-bold text-foreground">Ausgewählt</span>
            <span class="font-mono text-2xs text-muted-foreground">{{ selectedLine() }}</span>
          </div>
          <div class="overflow-hidden rounded-md border border-border">
            @for (address of selectedAddresses(); track address.addressId) {
              <div
                class="flex items-center gap-3 border-b border-border/60 px-4 py-3 last:border-b-0"
              >
                <ng-icon name="lucideMapPin" class="h-4 w-4 shrink-0 text-primary" />
                <span class="min-w-0 flex-1 truncate text-sm font-normal text-foreground">
                  {{ address.street }} {{ address.houseNumber }}
                </span>
                <span class="shrink-0 font-mono text-xs text-muted-foreground">
                  {{ address.postalCode }} {{ address.city }}
                </span>
                <button
                  type="button"
                  class="shrink-0 text-xs font-bold text-primary hover:text-primary/80"
                  (click)="toggled.emit(address)"
                >
                  Entfernen
                </button>
              </div>
            }
          </div>
        </div>
      }
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddressMultiSelect {
  private readonly host = inject(ElementRef<HTMLElement>);

  /** Result rows to show, already filtered by the host. */
  addresses = input<Address[]>([]);
  /** Every address currently picked — may include rows outside the filter. */
  selected = input<Address[]>([]);
  isLoading = input<boolean>(false);
  error = input<string | null>(null);
  /** Total available server-side, for the "x von y" hint. */
  totalCount = input<number | undefined>(undefined);

  searchChanged = output<string>();
  toggled = output<Address>();

  protected readonly open = signal(false);
  protected readonly query = signal('');

  private readonly selectedIdSet = computed(
    () => new Set(this.selected().map((a) => a.addressId)),
  );

  protected readonly selectedAddresses = computed(() => this.selected());

  protected readonly countLine = computed(() => {
    const shown = this.addresses().length;
    const total = this.totalCount();
    if (!total || total <= shown) return `${shown} Treffer`;
    return `${shown} von ${total} Treffern`;
  });

  protected readonly selectedLine = computed(
    () => `${this.selected().length} ausgewählt`,
  );

  protected isSelected(address: Address): boolean {
    return this.selectedIdSet().has(address.addressId);
  }

  protected onInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.query.set(value);
    this.open.set(true);
    this.searchChanged.emit(value);
  }

  /** A multi-select shouldn't close on pick, so close on an outside click instead. */
  protected onDocumentClick(event: MouseEvent): void {
    if (!this.open()) return;
    if (!this.host.nativeElement.contains(event.target as Node)) {
      this.open.set(false);
    }
  }
}
